import React, { useState, useEffect, useRef } from 'react';
import { Document, DocumentCategory, DocumentCategoryInfo, UploadDocumentData } from '../types';
import { documentsApi } from '../api';
import { useAuth } from '../AuthContext';
import { theme } from '../theme';

const DocumentArchive: React.FC = () => {
    const [documents, setDocuments] = useState<Document[]>([]);
    const [categories, setCategories] = useState<DocumentCategoryInfo[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<DocumentCategory | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [showUploadForm, setShowUploadForm] = useState(false);
    const { isAdmin } = useAuth();

    useEffect(() => {
        loadData();
    }, [selectedCategory]);

    const loadData = async () => {
        try {
            setIsLoading(true);
            const [docsData, catsData] = await Promise.all([
                documentsApi.getDocuments(selectedCategory),
                documentsApi.getCategories()
            ]);
            setDocuments(docsData);
            setCategories(catsData);
        } catch (err) {
            setError('Kunne ikke laste dokumenter');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Er du sikker på at du vil slette dette dokumentet?')) {
            return;
        }
        try {
            await documentsApi.deleteDocument(id);
            setDocuments(documents.filter(d => d.id !== id));
        } catch (err) {
            setError('Kunne ikke slette dokumentet');
        }
    };

    const handleDocumentUploaded = (newDoc: Document) => {
        setDocuments([newDoc, ...documents]);
        setShowUploadForm(false);
    };

    const handleDownload = (doc: Document) => {
        const token = localStorage.getItem('token');
        const downloadUrl = documentsApi.getDownloadUrl(doc.id);
        
        // Create a temporary link with auth header
        fetch(downloadUrl, {
            headers: { 'Authorization': `Bearer ${token}` }
        })
        .then(response => response.blob())
        .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = doc.fileName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        })
        .catch(err => setError('Kunne ikke laste ned dokumentet'));
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('nb-NO', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const getFileIcon = (contentType: string): string => {
        if (contentType.includes('pdf')) return '📄';
        if (contentType.includes('word') || contentType.includes('document')) return '📝';
        if (contentType.includes('excel') || contentType.includes('spreadsheet')) return '📊';
        if (contentType.includes('image')) return '🖼️';
        return '📎';
    };

    // Group documents by category
    const groupedDocuments = documents.reduce((groups, doc) => {
        const category = doc.categoryName;
        if (!groups[category]) {
            groups[category] = [];
        }
        groups[category].push(doc);
        return groups;
    }, {} as Record<string, Document[]>);

    if (isLoading) {
        return <div style={styles.loading}>Laster...</div>;
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Dokumentarkiv</h1>
                {isAdmin && (
                    <button
                        style={styles.uploadButton}
                        onClick={() => setShowUploadForm(true)}
                    >
                        + Last opp dokument
                    </button>
                )}
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.filters}>
                <button
                    style={{
                        ...styles.filterButton,
                        ...(selectedCategory === undefined ? styles.filterButtonActive : {})
                    }}
                    onClick={() => setSelectedCategory(undefined)}
                >
                    Alle
                </button>
                {categories.map(cat => (
                    <button
                        key={cat.value}
                        style={{
                            ...styles.filterButton,
                            ...(selectedCategory === cat.value ? styles.filterButtonActive : {})
                        }}
                        onClick={() => setSelectedCategory(cat.value)}
                    >
                        {cat.displayName}
                    </button>
                ))}
            </div>

            {showUploadForm && (
                <UploadDocumentForm
                    categories={categories}
                    onClose={() => setShowUploadForm(false)}
                    onUploaded={handleDocumentUploaded}
                />
            )}

            {documents.length === 0 ? (
                <div style={styles.empty}>Ingen dokumenter i arkivet ennå</div>
            ) : selectedCategory !== undefined ? (
                // Flat list when category is selected
                <div style={styles.documentList}>
                    {documents.map(doc => (
                        <DocumentCard
                            key={doc.id}
                            document={doc}
                            isAdmin={isAdmin}
                            onDownload={handleDownload}
                            onDelete={handleDelete}
                            getFileIcon={getFileIcon}
                            formatDate={formatDate}
                        />
                    ))}
                </div>
            ) : (
                // Grouped by category when showing all
                Object.entries(groupedDocuments).map(([category, docs]) => (
                    <div key={category} style={styles.categorySection}>
                        <h2 style={styles.categoryTitle}>{category}</h2>
                        <div style={styles.documentList}>
                            {docs.map(doc => (
                                <DocumentCard
                                    key={doc.id}
                                    document={doc}
                                    isAdmin={isAdmin}
                                    onDownload={handleDownload}
                                    onDelete={handleDelete}
                                    getFileIcon={getFileIcon}
                                    formatDate={formatDate}
                                />
                            ))}
                        </div>
                    </div>
                ))
            )}
        </div>
    );
};

interface DocumentCardProps {
    document: Document;
    isAdmin: boolean;
    onDownload: (doc: Document) => void;
    onDelete: (id: number) => void;
    getFileIcon: (contentType: string) => string;
    formatDate: (dateString: string) => string;
}

const DocumentCard: React.FC<DocumentCardProps> = ({
    document,
    isAdmin,
    onDownload,
    onDelete,
    getFileIcon,
    formatDate
}) => {
    return (
        <div style={styles.documentCard}>
            <div style={styles.docIcon}>{getFileIcon(document.contentType)}</div>
            <div style={styles.docInfo}>
                <div style={styles.docTitle}>{document.title}</div>
                {document.description && (
                    <div style={styles.docDescription}>{document.description}</div>
                )}
                <div style={styles.docMeta}>
                    <span>{document.fileName}</span>
                    <span>•</span>
                    <span>{document.fileSizeFormatted}</span>
                    <span>•</span>
                    <span>{formatDate(document.uploadedAt)}</span>
                </div>
            </div>
            <div style={styles.docActions}>
                <button
                    style={styles.downloadButton}
                    onClick={() => onDownload(document)}
                    title="Last ned"
                >
                    ⬇️ Last ned
                </button>
                {isAdmin && (
                    <button
                        style={styles.deleteDocButton}
                        onClick={() => onDelete(document.id)}
                        title="Slett"
                    >
                        🗑️
                    </button>
                )}
            </div>
        </div>
    );
};

interface UploadDocumentFormProps {
    categories: DocumentCategoryInfo[];
    onClose: () => void;
    onUploaded: (doc: Document) => void;
}

const UploadDocumentForm: React.FC<UploadDocumentFormProps> = ({ categories, onClose, onUploaded }) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState<DocumentCategory>(DocumentCategory.Annet);
    const [file, setFile] = useState<File | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            setFile(selectedFile);
            if (!title) {
                // Auto-fill title from filename (without extension)
                const nameWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, '');
                setTitle(nameWithoutExt);
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!file) {
            setError('Vennligst velg en fil');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const data: UploadDocumentData = {
                title,
                description: description || undefined,
                category
            };
            const doc = await documentsApi.uploadDocument(data, file);
            onUploaded(doc);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Kunne ikke laste opp dokumentet');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={styles.modal}>
            <div style={styles.modalContent}>
                <div style={styles.modalHeader}>
                    <h2>Last opp dokument</h2>
                    <button style={styles.closeButton} onClick={onClose}>✕</button>
                </div>

                {error && <div style={styles.error}>{error}</div>}

                <form onSubmit={handleSubmit} style={styles.form}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Fil *</label>
                        <div style={styles.fileInputWrapper}>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                style={styles.fileInput}
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
                            />
                            <button
                                type="button"
                                style={styles.fileButton}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                {file ? file.name : 'Velg fil...'}
                            </button>
                        </div>
                        <small style={styles.helpText}>
                            Tillatte formater: PDF, Word, Excel, bilder. Maks 50 MB.
                        </small>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Tittel *</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            style={styles.input}
                            placeholder="Dokumenttittel"
                            required
                            maxLength={200}
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Kategori</label>
                        <select
                            value={category}
                            onChange={(e) => setCategory(Number(e.target.value))}
                            style={styles.select}
                        >
                            {categories.map(cat => (
                                <option key={cat.value} value={cat.value}>
                                    {cat.displayName}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Beskrivelse</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            style={styles.textarea}
                            rows={3}
                            placeholder="Valgfri beskrivelse av dokumentet"
                            maxLength={500}
                        />
                    </div>

                    <div style={styles.formActions}>
                        <button type="button" style={styles.cancelButton} onClick={onClose}>
                            Avbryt
                        </button>
                        <button type="submit" style={styles.submitButton} disabled={isLoading || !file}>
                            {isLoading ? 'Laster opp...' : 'Last opp'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    container: {
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '16px',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px',
    },
    title: {
        margin: 0,
        color: '#333',
        fontSize: '24px',
    },
    uploadButton: {
        padding: '10px 20px',
        backgroundColor: theme.primaryColor,
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 'bold',
    },
    loading: {
        textAlign: 'center',
        padding: '48px',
        color: '#666',
    },
    error: {
        padding: '12px',
        backgroundColor: '#ffebee',
        color: '#c62828',
        borderRadius: '4px',
        marginBottom: '16px',
    },
    filters: {
        display: 'flex',
        gap: '8px',
        marginBottom: '20px',
        overflowX: 'auto',
        paddingBottom: '8px',
        WebkitOverflowScrolling: 'touch',
    },
    filterButton: {
        padding: '8px 14px',
        backgroundColor: '#f5f5f5',
        border: '1px solid #ddd',
        borderRadius: '20px',
        cursor: 'pointer',
        fontSize: '13px',
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
    filterButtonActive: {
        backgroundColor: theme.primaryColor,
        color: 'white',
        borderColor: theme.primaryColor,
    },
    empty: {
        textAlign: 'center',
        padding: '48px 16px',
        color: '#666',
        backgroundColor: 'rgba(255,255,255,0.9)',
        borderRadius: '8px',
    },
    categorySection: {
        marginBottom: '24px',
    },
    categoryTitle: {
        fontSize: '16px',
        color: '#333',
        marginBottom: '12px',
        paddingBottom: '8px',
        borderBottom: `2px solid ${theme.primaryColor}`,
    },
    documentList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    documentCard: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '14px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        flexWrap: 'wrap',
        border: '1px solid #eee',
    },
    docIcon: {
        fontSize: '32px',
        width: '48px',
        textAlign: 'center',
    },
    docInfo: {
        flex: 1,
        minWidth: 0,
    },
    docTitle: {
        fontWeight: 'bold',
        fontSize: '16px',
        color: '#333',
        marginBottom: '4px',
    },
    docDescription: {
        fontSize: '14px',
        color: '#666',
        marginBottom: '4px',
    },
    docMeta: {
        display: 'flex',
        gap: '8px',
        fontSize: '12px',
        color: '#999',
        flexWrap: 'wrap',
    },
    docActions: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        flexShrink: 0,
    },
    downloadButton: {
        padding: '8px 14px',
        backgroundColor: theme.primaryColor,
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '13px',
        whiteSpace: 'nowrap',
    },
    deleteDocButton: {
        padding: '8px',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        fontSize: '16px',
    },
    modal: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '16px',
    },
    modalContent: {
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '16px',
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflow: 'auto',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
    },
    closeButton: {
        background: 'none',
        border: 'none',
        fontSize: '20px',
        cursor: 'pointer',
        color: '#666',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    label: {
        fontSize: '14px',
        fontWeight: '500',
        color: '#333',
    },
    input: {
        padding: '10px 12px',
        fontSize: '16px',
        border: '1px solid #ddd',
        borderRadius: '4px',
    },
    select: {
        padding: '10px 12px',
        fontSize: '16px',
        border: '1px solid #ddd',
        borderRadius: '4px',
    },
    textarea: {
        padding: '10px 12px',
        fontSize: '16px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        resize: 'vertical',
        fontFamily: 'inherit',
    },
    fileInputWrapper: {
        position: 'relative',
    },
    fileInput: {
        position: 'absolute',
        opacity: 0,
        width: '100%',
        height: '100%',
        cursor: 'pointer',
    },
    fileButton: {
        width: '100%',
        padding: '12px',
        backgroundColor: '#f5f5f5',
        border: '2px dashed #ddd',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        color: '#666',
        textAlign: 'left',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    helpText: {
        color: '#666',
        fontSize: '12px',
        marginTop: '4px',
    },
    formActions: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        marginTop: '8px',
    },
    cancelButton: {
        padding: '10px 20px',
        backgroundColor: '#f5f5f5',
        border: '1px solid #ddd',
        borderRadius: '4px',
        cursor: 'pointer',
    },
    submitButton: {
        padding: '10px 20px',
        backgroundColor: theme.primaryColor,
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontWeight: 'bold',
    },
};

export default DocumentArchive;
